import { Fragment, useRef } from 'react';

import { CellLayout, EmptyState, InteractiveTable, LoadingTableRows, Table } from '@ingrid/luterkort-react';
import { ShippingLogo } from '@ingrid/react-shipping-logo';

import { Ellipsis } from '~src/components/Ellipsis/Ellipsis';
import { useTranslation } from '~src/i18n/use-translation';

import { Menu } from '../menu';
import { CarrierContractDetail } from '../queries';
import { ActionPanel } from './action-panel';
import { DetailsDrawer } from './details-drawer';

interface Props {
  carrierContracts: CarrierContractDetail[];
  loadRowCount: number;
  isEmpty: boolean;
  loading: boolean;
}

export function ListTable(props: Props) {
  const { carrierContracts, isEmpty, loadRowCount, loading } = props;

  const { highlightedKey } = InteractiveTable.useContext();
  const { t } = useTranslation();

  const contract = carrierContracts.find(c => c.contract_id === highlightedKey);
  const tableRef = useRef<HTMLTableElement>(null);

  if (isEmpty) {
    return <EmptyState title={t('carrierContracts.list.notFound')} />;
  }

  return (
    <Fragment>
      <InteractiveTable.Table ref={tableRef}>
        <thead>
          <Table.Row>
            <Table.HeadCell css={{ width: '30px' }}>
              <InteractiveTable.HeadCheckbox />
            </Table.HeadCell>
            <Table.HeadCell css={{ width: '40%' }}>{t('common.name')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '25%' }}>{t('common.description')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '25' }}>{t('common.carrier')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '35px' }} />
          </Table.Row>
        </thead>
        <tbody>
          {carrierContracts.map(contract => (
            <InteractiveTable.Row key={contract.contract_id} value={contract.contract_id!}>
              <Table.Cell>
                <InteractiveTable.Checkbox value={contract.contract_id!} />
              </Table.Cell>
              <Table.Cell>
                <Ellipsis>{contract.name}</Ellipsis>
              </Table.Cell>
              <Table.Cell>
                <Ellipsis>{contract.description}</Ellipsis>
              </Table.Cell>
              <Table.Cell>
                <CellLayout.Row>
                  <ShippingLogo method={contract.carrier_products?.[0].shipping_method} width={32} />
                  <Ellipsis> {contract.carrier_name} </Ellipsis>
                </CellLayout.Row>
              </Table.Cell>
              <Table.Cell>
                <Menu listing>
                  <Menu.ShowDetailItem contractId={contract.contract_id!} />
                  <Menu.EditItem contractId={contract.contract_id!} />
                  <Menu.DuplicateItem contractId={contract.contract_id!} />
                  <Menu.DeleteItem contractId={contract.contract_id!} />
                </Menu>
              </Table.Cell>
            </InteractiveTable.Row>
          ))}
          {loading && <LoadingTableRows tableRef={tableRef} rowCount={loadRowCount} />}
        </tbody>
      </InteractiveTable.Table>

      <ActionPanel />

      <DetailsDrawer contract={contract} />
    </Fragment>
  );
}
